import Accordion from '@/common/components/Accordion';
import { BadgeInfoIcon, CircleChevronDown } from 'lucide-react';
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage';
import NotificationsConfigurationsLoadingState from '@/features/notifications/components/NotificationsConfigurationsLoadingState';
import { Tooltip } from 'react-tooltip';
import { getNotificationProviderOptionFromPossibleProviders } from '@/features/notifications/constants/providers.constant';
import { NotificationConfigurationsAvailableNotificationData, NotificationConfigurationsData } from '@/features/notifications/types/configurations.type';
import { Mapper } from '@/common/services/mapper';
import { NotificationsConfigurationsTransformer } from '@/features/notifications/services/notificationsConfigurationsTransformer';
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type';
import UpdateNotificationsModal from '../../Update/components/UpdateNotificationsModal';
import { useEstablishmentNotificationsConfigurationsAvailableNotifications } from '@/features/establishments/hooks/politics/useEstablishmentNotificationConfigurationsAvailableNotifications';
import { useEstablishmentNotificationsConfigurations } from '@/features/establishments/hooks/politics/useEstablishmentNotificationConfigurations';

const NotificationsSection = ({ establishmentCompany }: { establishmentCompany?: EstablishmentCompanyData }) => {
  const {
    data: availableNotifications,
    isPending: isAvailableNotificationsPending,
  } = useEstablishmentNotificationsConfigurationsAvailableNotifications<NotificationConfigurationsAvailableNotificationData[]>({
    filters: {
      companyId: establishmentCompany?.id,
    },
  });

  const {
    data: notificationsConfigurations,
    isPending: isNotificationsConfigurationsPending,
  } = useEstablishmentNotificationsConfigurations<Record<string, NotificationConfigurationsData>>({
    filters: {
      companyId: establishmentCompany?.id,
    },
    mapper: Mapper.mapWithTransformer({ transformer: NotificationsConfigurationsTransformer.strategyTransformer() })
  });

  return (
    <Accordion
      defaultOpen
      head={(
        <div className="flex items-center gap-x-3">
          <div onClick={(e) => e.stopPropagation()}>
            <UpdateNotificationsModal
              establishmentCompany={establishmentCompany}
              availableNotifications={availableNotifications}
              configurations={notificationsConfigurations}
            />
          </div>

          <p className='text-xl'>
            Notificações
          </p>
        </div>
      )}
      triggerClassName="py-0"
      bodyClassName="pt-4 pb-0"
      body={(
        <>
          {(isAvailableNotificationsPending || isNotificationsConfigurationsPending) && <NotificationsConfigurationsLoadingState />}

          {((!isAvailableNotificationsPending && !availableNotifications) || Number(availableNotifications?.length)) === 0 && (
            <EmptyDataMessage
              className='h-[260px] overflow-hidden -mt-20'
              message='Não há notificações para serem configuradas.'
            />
          )}

          {!isNotificationsConfigurationsPending && notificationsConfigurations && (
            <div className="flex flex-col gap-4">
              {availableNotifications?.map((notification) => {
                const config = notificationsConfigurations?.[notification.notification_klass];
                const providers = getNotificationProviderOptionFromPossibleProviders(notification.possible_providers);

                return (
                  <div key={notification.notification_klass} className="flex items-center gap-2">
                    <p className="text-sm text-white-800">
                      {notification.display_name}:
                    </p>

                    <BadgeInfoIcon
                      strokeWidth={1.5}
                      color="var(--warning)"
                      className="size-5"
                      data-tooltip-id={`notification-setting-${notification.notification_klass}-tooltip`}
                      data-tooltip-place="top-start"
                    />

                    <Tooltip
                      id={`notification-setting-${notification.notification_klass}-tooltip`}
                      className="max-w-[234px] opacity-100 backdrop-blur-3xl"
                    >
                      {notification.description}
                    </Tooltip>

                    <div className="flex items-center gap-1">
                      <CircleChevronDown strokeWidth={1.5} className="size-5" />

                      <p className="text-sm text-white-400">
                        {Number(config?.provider_klasses?.length || 0) === 0
                          ? 'Todos'
                          : config?.provider_klasses?.map((provider) => providers[provider as never]).join(', ')}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    />
  )
}

export default NotificationsSection;
